import { Action } from '@ngrx/store';
import { CareAppListActionFactory } from './action-factory';
import { ResponseSuccessAuthLogoutAction } from './actions';
import { DataListState, INITIAL_DATA_LIST_STATE } from './data-list.state';

export class ReducerStateFactory<T> {
  readonly initialState: DataListState<T> = {
    ...INITIAL_DATA_LIST_STATE,
  };

  constructor(public actionClass: CareAppListActionFactory<T>) {}

  /**
   * The reducer function
   */
  public reducer(state = this.initialState, action: Action): DataListState<T> {
    const typedAction = <any>action;

    const allResult = this.allReducer(state, action);
    if (allResult) {
      return allResult;
    }

    const allPaginatedResult = this.allPaginatedReducer(state, action);
    if (allPaginatedResult) {
      return allPaginatedResult;
    }

    const entityResult = this.entityReducer(state, action);
    if (entityResult) {
      return entityResult;
    }

    switch (action.type) {
      case this.actionClass.RESPONSE_FAIL:
        const error = typedAction.payload.code || typedAction.payload;
        return { ...state, error };
      case this.actionClass.SELECT_BY_ID:
        return { ...state, selectedId: typedAction.payload.id };
      case this.actionClass.CLEAR_PAGINATION: {
        const paginators = { ...state.paginators };
        delete paginators[(<any>action).paginator];
        return { ...state, paginators };
      }
      case this.actionClass.CLEAR_PAGINATION_IDS: {
        const paginators = { ...state.paginators };
        const thisPaginator = { ...paginators[(<any>action).paginator] };
        thisPaginator.ids = [];
        thisPaginator.complete = false;
        thisPaginator.loading = false;
        return {
          ...state,
          paginators: { ...paginators, [(<any>action).paginator]: thisPaginator },
        };
      }
      case ResponseSuccessAuthLogoutAction.type:
        return this.initialState;
      default:
        return state;
    }
  }

  protected allReducer(state, action: any): DataListState<T> {
    switch (action.type) {
      case this.actionClass.REQUEST_ALL:
        return { ...state, loadingAll: true };
      case this.actionClass.PAGE_CHANGE:
        return { ...state, page: action.payload };
      case this.actionClass.PAGE_CHANGE_BY_ID: {
        if (action.payload?.paginatorId != null && action.payload?.page != null) {
          const id = action.payload.paginatorId;
          return {
            ...state,
            paginators: {
              ...(state.paginators ?? {}),
              [id]: { ...state.paginators[id], page: action.payload.page },
            },
          };
        }
      }
      case this.actionClass.UPDATE_FILTER:
        return {
          ...state,
          filter: {
            ...action.payload,
            order: action.payload?.order || (state.filter || {}).order,
          },
          page: { ...INITIAL_DATA_LIST_STATE.page },
        };
      case this.actionClass.UPDATE_FILTER_BY_ID: {
        const paginatorId = action.payload.paginatorId;
        if (paginatorId != null) {
          const existingFilter = state.paginators[paginatorId]?.filter ?? {};

          return {
            ...state,
            paginators: {
              ...state.paginators,
              [paginatorId]: {
                ...state.paginators[paginatorId],
                filter: {
                  ...action.payload,
                  order: action.payload?.order || (existingFilter || {}).order,
                  paginatorId: undefined,
                },
              },
            },
          };
        }
        return { ...state };
      }
      case this.actionClass.RESET_PAGE:
        return { ...state, page: { ...INITIAL_DATA_LIST_STATE.page } };
      case this.actionClass.REQUEST_ALL:
        return { ...state, loadingAll: true };
      case this.actionClass.RESPONSE_SUCCESS_COUNT:
        return { ...state, page: { ...state.page, length: action.payload } };
      case this.actionClass.REQUEST_SEARCH:
        return { ...state, searching: true, searchResults: [] };
      case this.actionClass.RESPONSE_SUCCESS_SEARCH: {
        const searchResults = action.payload.map((org) => org.id);
        const ids = Array.from(new Set(state.ids.concat(searchResults)));
        const entities = {
          ...state.entities,
          ...action.payload.reduce(
            (acc: any, entity: any) => ({ ...acc, [entity.id]: entity }),
            {}
          ),
        };
        return { ...state, searching: false, ids, entities, searchResults };
      }
      case this.actionClass.RESPONSE_FAIL_SEARCH:
        return { ...state, searching: false };

      case this.actionClass.RESPONSE_SUCCESS_ALL: {
        const ids = action.payload.map((org) => org.id);
        const time = new Date().valueOf();
        const updatedAt = action.payload.reduce(
          (acc: any, entity: any) => ({ ...acc, [entity.id]: time }),
          {}
        );
        const entities = action.payload.reduce(
          (acc: any, entity: any) => ({ ...acc, [entity.id]: entity }),
          {}
        );
        return { ...state, loadingAll: false, ids, entities, updatedAt };
      }

      case this.actionClass.RESPONSE_FAIL_ALL: {
        return { ...state, loadingAll: false, error: action.payload };
      }
    }
  }

  private allPaginatedReducer(state, action: any): DataListState<T> {
    switch (action.type) {
      case this.actionClass.REQUEST_ALL_PAGINATED: {
        const paginators = state.paginators || {};
        const paginator = {
          [action.paginator]: { ...paginators[action.paginator], loading: true },
        };
        return { ...state, paginators: { ...paginators, ...paginator } };
      }

      case this.actionClass.RESPONSE_SUCCESS_ALL_PAGINATED: {
        const { items, filter, paginator } = action.payload;
        const time = new Date().valueOf();
        const updatedAt = items.reduce(
          (acc: any, entity: any) => ({ ...acc, [entity.id]: time }),
          {}
        );
        const entities = items.reduce(
          (acc: any, entity: any) => ({ ...acc, [entity.id]: entity }),
          {}
        );
        const paginators = state.paginators;
        const complete = items.length < filter.limit;
        const skip = filter.skip || 0;
        const ids = state.paginators[paginator] ? state.paginators[paginator].ids || [] : [];
        const paginatorIds = [...ids];

        const newIds = items.map((item) => item.id);
        const allIds = Array.from(new Set([...state.ids, ...newIds]));

        for (let i = 0; i < items.length; i++) {
          paginatorIds[i + skip] = items[i].id;
        }

        return {
          ...state,
          entities: { ...state.entities, ...entities },
          ids: allIds,
          updatedAt: { ...state.updatedAt, ...updatedAt },
          paginators: {
            ...paginators,
            [paginator]: { ids: paginatorIds, complete, loading: false },
          },
        };
      }

      case this.actionClass.RESPONSE_FAIL_ALL_PAGINATED: {
        const paginators = state.paginators;
        const paginator = {
          [action.paginator]: {
            ...paginators[action.paginator],
            loading: false,
            error: action.payload,
          },
        };

        return { ...state, paginators: { ...paginators, ...paginator } };
      }
    }
  }

  private entityReducer(state, action: any): DataListState<T> {
    switch (action.type) {
      // Request
      case this.actionClass.REQUEST_CREATE: {
        return { ...state, creating: true };
      }
      case this.actionClass.REQUEST_SAVE:
      case this.actionClass.REQUEST_BY_ID:
      case this.actionClass.REQUEST_DESTROY_BY_ID:
        return {
          ...state,
          loadingEntities: {
            ...state.loadingEntities,
            [action.payload.id]: true,
          },
        };

      case this.actionClass.RESPONSE_SUCCESS_BY_ID_CACHED:
        return {
          ...state,
          loadingEntities: {
            ...state.loadingEntities,
            [action.payload.id]: false,
          },
        };

      // Response Success
      case this.actionClass.RESPONSE_SUCCESS_SAVE:
      case this.actionClass.RESPONSE_SUCCESS_CREATE:
      case this.actionClass.RESPONSE_SUCCESS_BY_ID: {
        const ids = Array.from(new Set([...state.ids, action.payload.id]));
        const entities = {
          ...state.entities,
          [action.payload.id]: action.payload,
        };
        const updatedAt = {
          ...state.updatedAt,
          [action.payload.id]: new Date().valueOf(),
        };
        return {
          ...state,
          loadingEntities: {
            ...state.loadingEntities,
            [action.payload.id]: false,
          },
          updatedAt,
          creating: false,
          ids: ids,
          entities: entities,
        };
      }
      case this.actionClass.RESPONSE_FAIL_CREATE:
        return {
          ...state,
          creating: false,
        };

      case this.actionClass.RESPONSE_SUCCESS_DESTROY_BY_ID:
        const ids = new Set(state.ids);
        ids.delete(action.payload.id);
        const entities = { ...state.entities };
        delete entities[action.payload.id];
        const updatedAt = { ...state.updatedAt };
        delete updatedAt[action.payload.id];
        return {
          ...state,
          ids: Array.from(ids),
          entities,
          loadingEntities: {
            ...state.loadingEntities,
            [action.payload.id]: false,
          },
          updatedAt,
        };

      // Response Fail
      case this.actionClass.RESPONSE_FAIL_DESTROY_BY_ID:
      case this.actionClass.RESPONSE_FAIL_BY_ID:
      case this.actionClass.RESPONSE_FAIL_SAVE:
        return {
          ...state,
          error: action.payload,
          loadingEntities: {
            ...state.loadingEntities,
            [action.payload.id]: false,
          },
        };
    }
  }
}
